import { useEffect, useRef, useState } from 'react';
import { Pen, Highlighter, Eraser, Trash2, X } from 'lucide-react';
import { connectSocket } from '@/lib/socket';

type Tool = 'pen' | 'highlighter' | 'eraser';
interface Point {
  x: number;
  y: number;
}
interface DrawEvent {
  meetingId: string;
  from: Point;
  to: Point;
  color: string;
  width: number;
  tool: Tool;
}

const COLORS = ['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#ffffff', '#0f172a'];

/** Transparent canvas overlay for live annotation on top of the video stage. */
export default function AnnotationCanvas({ meetingId, onClose }: { meetingId: string; onClose: () => void }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const last = useRef<Point | null>(null);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[0]);

  // resize canvas to its container
  useEffect(() => {
    const canvas = canvasRef.current!;
    const resize = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const strokeStyleFor = (t: Tool, c: string): { color: string; width: number; alpha: number } => {
    if (t === 'highlighter') return { color: c, width: 14, alpha: 0.35 };
    if (t === 'eraser') return { color: '#000', width: 24, alpha: 1 };
    return { color: c, width: 3, alpha: 1 };
  };

  const paint = (from: Point, to: Point, c: string, w: number, t: Tool) => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const style = strokeStyleFor(t, c);
    ctx.globalCompositeOperation = t === 'eraser' ? 'destination-out' : 'source-over';
    ctx.globalAlpha = style.alpha;
    ctx.strokeStyle = c;
    ctx.lineWidth = w;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(from.x * canvas.width, from.y * canvas.height);
    ctx.lineTo(to.x * canvas.width, to.y * canvas.height);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  };

  // remote events
  useEffect(() => {
    const socket = connectSocket();
    const onDraw = (e: DrawEvent) => paint(e.from, e.to, e.color, e.width, e.tool);
    const onClear = () => {
      const c = canvasRef.current!;
      c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    };
    socket.on('annotation:draw', onDraw);
    socket.on('annotation:clear', onClear);
    return () => {
      socket.off('annotation:draw', onDraw);
      socket.off('annotation:clear', onClear);
    };
  }, []);

  const toNorm = (e: React.PointerEvent): Point => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
  };

  const onDown = (e: React.PointerEvent) => {
    drawing.current = true;
    last.current = toNorm(e);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drawing.current || !last.current) return;
    const pt = toNorm(e);
    const style = strokeStyleFor(tool, color);
    paint(last.current, pt, color, style.width, tool);
    connectSocket().emit('annotation:draw', {
      meetingId,
      from: last.current,
      to: pt,
      color,
      width: style.width,
      tool,
    } satisfies DrawEvent);
    last.current = pt;
  };
  const onUp = () => {
    drawing.current = false;
    last.current = null;
  };

  const clearAll = () => {
    const c = canvasRef.current!;
    c.getContext('2d')!.clearRect(0, 0, c.width, c.height);
    connectSocket().emit('annotation:clear', { meetingId });
  };

  return (
    <div className="absolute inset-0 z-20">
      <canvas
        ref={canvasRef}
        className="h-full w-full cursor-crosshair touch-none"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
      />
      <div className="absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-900/90 px-3 py-2 shadow-lg">
        {([['pen', Pen], ['highlighter', Highlighter], ['eraser', Eraser]] as const).map(([t, Icon]) => (
          <button
            key={t}
            className={`rounded-full p-1.5 ${tool === t ? 'bg-brand-600 text-white' : 'text-slate-300'}`}
            onClick={() => setTool(t)}
          >
            <Icon size={16} />
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-700" />
        {COLORS.map((c) => (
          <button
            key={c}
            className={`h-5 w-5 rounded-full border ${color === c ? 'ring-2 ring-white' : 'border-slate-600'}`}
            style={{ background: c }}
            onClick={() => setColor(c)}
          />
        ))}
        <span className="mx-1 h-5 w-px bg-slate-700" />
        <button className="rounded-full p-1.5 text-slate-300 hover:text-red-400" onClick={clearAll}>
          <Trash2 size={16} />
        </button>
        <button className="rounded-full p-1.5 text-slate-300 hover:text-white" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
